import { alpha3ToAlpha2 } from "@utils/countryCodeMap";

const ALPHA2_LENGTH = 2;
const ALPHA3_LENGTH = 3;
const RUSSIAN_LOCALE = "ru";
const ENGLISH_LOCALE = "en";
const UKRAINIAN_LOCALE = "ua";
// App language code → BCP 47 tag for Intl.
const INTL_LOCALES = { [UKRAINIAN_LOCALE]: "uk" };

const displayNamesCache = new Map();
const alpha2CountsCache = new WeakMap();

const getAlpha2Counts = (countries) => {
  if (alpha2CountsCache.has(countries)) {
    return alpha2CountsCache.get(countries);
  }

  const counts = new Map();

  countries.forEach((item) => {
    const code = getCountryAlpha2(item);

    if (code) {
      counts.set(code, (counts.get(code) || 0) + 1);
    }
  });

  alpha2CountsCache.set(countries, counts);

  return counts;
};

const getDisplayNames = (locale) => {
  if (displayNamesCache.has(locale)) {
    return displayNamesCache.get(locale);
  }

  let displayNames = null;

  try {
    displayNames = new Intl.DisplayNames([INTL_LOCALES[locale] || locale], {
      type: "region",
    });
  } catch {
    displayNames = null;
  }

  displayNamesCache.set(locale, displayNames);

  return displayNames;
};

/**
 * Two-letter code of a country from the backend, upper case, or "".
 *
 * @param {object} country
 * @returns {string}
 */
export const getCountryAlpha2 = (country) => {
  if (!country) {
    return "";
  }

  const directCode = country.code_alpha2 || country.alpha2 || country.iso2 || country.code2;

  if (typeof directCode === "string" && directCode.trim()) {
    return directCode.trim().toUpperCase();
  }

  const code = typeof country.code === "string" ? country.code.trim().toUpperCase() : "";

  if (code.length === ALPHA2_LENGTH) {
    return code;
  }

  if (code.length === ALPHA3_LENGTH) {
    return (alpha3ToAlpha2[code] || "").toUpperCase();
  }

  return "";
};

/**
 * Name of a country in the current language: ru and en come from the backend,
 * other languages from Intl.DisplayNames with the backend name as a fallback.
 * Several backend entries share one code (Christmas Island → AU), so the Intl
 * name is used only when the code is unique in the list or label_en matches
 * the English Intl name.
 *
 * @param {object} country
 * @param {string} locale
 * @param {object[]} [countries] - the full list from the backend
 * @returns {string}
 */
export const getCountryName = (country, locale, countries) => {
  if (!country) {
    return "";
  }

  if (locale === RUSSIAN_LOCALE) {
    return country.label_ru || country.label_en || "";
  }

  const fallback = country.label_en || country.label_ru || "";

  if (locale === ENGLISH_LOCALE) {
    return fallback;
  }

  const alpha2 = getCountryAlpha2(country);
  const displayNames = alpha2 ? getDisplayNames(locale) : null;
  const englishNames = alpha2 ? getDisplayNames(ENGLISH_LOCALE) : null;

  if (!displayNames || !englishNames) {
    return fallback;
  }

  try {
    const isUniqueCode =
      Array.isArray(countries) && getAlpha2Counts(countries).get(alpha2) === 1;
    const englishName = englishNames.of(alpha2) || "";
    const isCanonical =
      englishName.trim().toLowerCase() ===
      String(country.label_en || "").trim().toLowerCase();

    if (!isUniqueCode && !isCanonical) {
      return fallback;
    }

    const name = displayNames.of(alpha2);

    return name && name !== alpha2 ? name : fallback;
  } catch {
    return fallback;
  }
};

export const normalizeAlpha2 = (code) => {
  if (!code || typeof code !== "string") return "";

  const trimmed = code.trim();

  if (trimmed.length !== 2) return "";

  return trimmed.toUpperCase();
};

export const toAlpha2CountryCode = (newValue, countries = []) => {
  if (!newValue) return "";

  if (typeof newValue === "string") {
    const fromAlpha2 = normalizeAlpha2(newValue);

    if (fromAlpha2) return fromAlpha2;

    const alpha3 = newValue.trim();

    if (alpha3.length === 3) {
      const mapped = alpha3ToAlpha2[alpha3.toUpperCase()];
      const mappedAlpha2 = normalizeAlpha2(mapped);

      if (mappedAlpha2) return mappedAlpha2;
    }

    const countryItem =
      countries.find((c) => c.code === newValue) ||
      countries.find(
        (c) =>
          c.code_alpha2 === newValue ||
          c.alpha2 === newValue ||
          c.iso2 === newValue ||
          c.code2 === newValue,
      );

    const fromItem =
      normalizeAlpha2(countryItem?.code_alpha2) ||
      normalizeAlpha2(countryItem?.alpha2) ||
      normalizeAlpha2(countryItem?.iso2) ||
      normalizeAlpha2(countryItem?.code2) ||
      normalizeAlpha2(countryItem?.code);

    return fromItem || "";
  }

  const direct =
    newValue.code_alpha2 ||
    newValue.alpha2 ||
    newValue.iso2 ||
    newValue.code2 ||
    "";

  const directAlpha2 = normalizeAlpha2(direct);

  if (directAlpha2) return directAlpha2;

  const codeAlpha2 = normalizeAlpha2(newValue.code);

  if (codeAlpha2) return codeAlpha2;

  if (typeof newValue.code === "string" && newValue.code.length === 3) {
    const mapped = alpha3ToAlpha2[newValue.code.toUpperCase()];
    const mappedAlpha2 = normalizeAlpha2(mapped);

    if (mappedAlpha2) return mappedAlpha2;

    const by3 =
      countries.find((c) => c.code === newValue.code) ||
      countries.find(
        (c) =>
          c.code_alpha3 === newValue.code ||
          c.alpha3 === newValue.code ||
          c.iso3 === newValue.code,
      );

    const alpha2 =
      by3?.code_alpha2 ||
      by3?.alpha2 ||
      by3?.iso2 ||
      by3?.code2 ||
      (typeof by3?.code === "string" && by3.code.length === 2
        ? by3.code
        : "");

    const alpha2Normalized = normalizeAlpha2(alpha2);

    if (alpha2Normalized) return alpha2Normalized;
  }

  return "";
};

export const findCountryByAlpha2 = (alpha2Code, countries = []) => {
  const normalized = normalizeAlpha2(alpha2Code);

  if (!normalized) return null;

  const deriveAlpha2FromCountryItem = (country) => {
    const direct =
      normalizeAlpha2(country.code_alpha2) ||
      normalizeAlpha2(country.alpha2) ||
      normalizeAlpha2(country.iso2) ||
      normalizeAlpha2(country.code2) ||
      normalizeAlpha2(country.code);

    if (direct) return direct;

    if (typeof country.code === "string" && country.code.trim().length === 3) {
      const mapped = alpha3ToAlpha2[country.code.trim().toUpperCase()];
      return normalizeAlpha2(mapped) || "";
    }

    return "";
  };

  return (
    countries.find((country) => normalizeAlpha2(country.code) === normalized) ||
    countries.find(
      (country) => normalizeAlpha2(country.code_alpha2) === normalized,
    ) ||
    countries.find(
      (country) => normalizeAlpha2(country.alpha2) === normalized,
    ) ||
    countries.find((country) => normalizeAlpha2(country.iso2) === normalized) ||
    countries.find((country) => normalizeAlpha2(country.code2) === normalized) ||
    countries.find(
      (country) => deriveAlpha2FromCountryItem(country) === normalized,
    ) ||
    null
  );
};